import AddIcon from "@mui/icons-material/Add";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { Fragment, type ReactNode, useId } from "react";
import { Link as RouterLink } from "react-router";
import { ChangeHighlightTint, prefersReducedMotion } from "_shared/_views/highlights/change-highlight-tint";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import { TOUCH_TARGET, touchTargetQuery } from "_shared/_views/touch-target";
import type { GrantRowModel, GrantSource } from "permissions/_views/grant-row/grant-row-model";
import LevelControl from "permissions/_views/grant-row/level-control";
import {
  FROM,
  GRANT,
  GRANT_AGAIN,
  type GrantLevel,
  WIDEN_TO_ORGANIZATION,
  accessibleNames,
  kindAllows,
  kindLabel,
} from "permissions/_views/wording/permission-wording";

/**
 * One grant, drawn the same way on the person page, the record page, in search results and in the
 * add and template dialogs (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "The grant row").
 *
 * The fold order belongs to the component rather than to each page:
 *
 * - **Below md (900 px)**: a card that wraps — the kind, the switch and the actions; then the target;
 *   then the level chip, with the source beside it while it fits and beneath it once it does not.
 * - **md, 900 px and up**: one line — the kind with the target beneath it, the level, the source, the
 *   switch and the actions. The rows of one `GrantRowTable` share its columns as subgrids, so the
 *   level, source and switch line up from row to row as a table's columns do.
 * - **lg, 1200 px and up**: six parts in one line, the target with a column of its own.
 *
 * It carries the ARIA table roles — a row, its row header and its cells — so a screen reader can move
 * through a list of grants cell by cell. A row is drawn only inside a `GrantRowTable`. Rows are not
 * clickable; their links and controls are. Nothing is hover-only: a lock's explanation is visible to
 * a screen reader through `aria-describedby` and to everyone else in a tooltip that opens on tap and
 * on focus.
 */

// The kind keeps a floor of its own: the other columns are sized to their widest content, and
// without it one long source or button in a table squeezes every kind name to a word per line.
const KIND_MIN = "min(16rem, 40%)";

const Table = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "grid",
    columnGap: theme.spacing(1.5),
    gridTemplateColumns: `minmax(${KIND_MIN}, 1fr) auto auto auto auto`,
  },
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: `minmax(${KIND_MIN}, 2fr) minmax(0, 1fr) auto auto auto auto`,
  },
}));

const Row = styled("div")(({ theme }) => ({
  // Below md the row is a wrapping flex line rather than a grid: a card's level chip must keep its
  // whole text, and the source should sit beside it only when there is room, which is what wrapping
  // decides and a fixed grid cannot.
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(0.75, 1.5),
  padding: theme.spacing(1.5, 2),
  // The change tint is laid over the row from inside it.
  position: "relative",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": { borderBottom: "none" },
  // Ends the first line after the actions, whatever their widths.
  "&::after": { content: '""', flexBasis: "100%", order: 1 },
  [theme.breakpoints.up("md")]: {
    display: "grid",
    gridColumn: "1 / -1",
    gridTemplateColumns: "subgrid",
    gridTemplateAreas: `
      "kind level source state actions"
      "target level source state actions"
    `,
    "&::after": { display: "none" },
  },
  [theme.breakpoints.up("lg")]: {
    gridTemplateAreas: `"kind target level source state actions"`,
  },
  "&[data-state='removed'] [data-part='kind'] .grant-row-kind-name": { color: theme.palette.text.secondary },
}));

const Part = styled("div")({ minWidth: 0 });

// A basis wide enough to read, so a wide Grant button drops to a line of its own rather than
// squeezing the kind to a word per line.
const KindPart = styled(Part)({ gridArea: "kind", flex: "1 1 12rem", order: 0 });
const TargetPart = styled(Part)({ gridArea: "target", flexBasis: "100%", order: 2, "&:empty": { display: "none" } });
const LevelPart = styled(Part)({ gridArea: "level", flex: "0 0 auto", maxWidth: "100%", order: 3 });
const SourcePart = styled(Part)(({ theme }) => ({
  gridArea: "source",
  flex: "1 1 10rem",
  order: 3,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(0.5, 1),
}));
const StatePart = styled(Part)({ gridArea: "state", order: 0, display: "flex", justifyContent: "flex-end" });
const ActionsPart = styled(Part)({ gridArea: "actions", order: 0, display: "flex", justifyContent: "flex-end", minWidth: TOUCH_TARGET });
/** A whole line under the row, at every width: what went wrong with the last change to it. */
const AlertPart = styled(Part)({ flexBasis: "100%", order: 4, gridColumn: "1 / -1" });

const TouchSwitch = styled(Switch)(({ theme }) => ({
  [touchTargetQuery(theme)]: { minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET },
}));

const QuietButton = styled(Button)(({ theme }) => ({
  [touchTargetQuery(theme)]: { minHeight: TOUCH_TARGET },
}));

/** A list of grant rows: the grid the rows share, and a table to assistive technology. */
export function GrantRowTable(props: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <Table role="table" aria-label={props.label}>
      {props.children}
    </Table>
  );
}

function LegacySource(props: Readonly<{ label: string; explanation: string }>) {
  const explanationId = useId();
  return (
    <>
      <Tooltip title={props.explanation} enterTouchDelay={0} describeChild>
        <Typography variant="body2" color="text.secondary" tabIndex={0} aria-describedby={explanationId}>
          {props.label}
        </Typography>
      </Tooltip>
      <span id={explanationId} style={visuallyHidden}>
        {props.explanation}
      </span>
    </>
  );
}

/**
 * A grantor the directory has not named: skeleton text while it loads; otherwise when it was granted,
 * with the person state beneath ("No longer a member"), and Retry where the lookup failed.
 */
function UnnamedGrantor(
  props: Readonly<{ source: Extract<GrantSource, { type: "unnamed-grantor" }>; kind: string; personName: string; onRetry?: () => void }>,
) {
  const { source, kind, personName, onRetry } = props;
  if (source.state === "loading") return <Skeleton variant="text" width="8rem" aria-label={accessibleNames.loadingGrantor(kind, personName)} />;
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {source.label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1 }}>
        <Typography variant="caption" color="text.secondary" component="p">
          {source.stateText}
        </Typography>
        {source.state === "failed" && onRetry && (
          <Button size="small" onClick={onRetry} aria-label={accessibleNames.retryGrantor(kind, personName)}>
            Retry
          </Button>
        )}
      </Box>
    </Box>
  );
}

function SourceText(props: Readonly<{ source: GrantSource; kind: string; personName: string; onRetryGrantor?: () => void }>) {
  const { source } = props;
  switch (source.type) {
    case "unnamed-grantor":
      return <UnnamedGrantor source={source} kind={props.kind} personName={props.personName} onRetry={props.onRetryGrantor} />;
    case "grantor":
    case "roster":
      return (
        <Typography variant="body2" color="text.secondary">
          {source.label}
        </Typography>
      );
    case "system":
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            {source.label}
          </Typography>
          {source.note && (
            <Typography variant="caption" color="text.secondary" component="p">
              {source.note}
            </Typography>
          )}
        </Box>
      );
    case "legacy":
      return <LegacySource label={source.label} explanation={source.explanation} />;
    case "template":
      return (
        <Typography variant="body2" color="text.secondary">
          {source.href ? (
            <Link component={RouterLink} to={source.href}>
              {source.label}
            </Link>
          ) : (
            source.label
          )}
        </Typography>
      );
    case "parents":
      return (
        <Typography variant="body2" color="text.secondary">
          {FROM}{" "}
          {source.parents.map((parent, index) => (
            <Fragment key={`${parent.label}-${index}`}>
              {index > 0 && " + "}
              {parent.rowId ? <Link href={`#${parent.rowId}`}>{parent.label}</Link> : parent.label}
            </Fragment>
          ))}
        </Typography>
      );
  }
}

export type GrantRowProps = Readonly<{
  row: GrantRowModel;
  /**
   * Which page the row is on. On a person page the row header is the kind; on a record page it is
   * the person, passed as `personHeader`, and the kind is the page's own.
   */
  subject: "person" | "record";
  /** The person the grant is for, which every control's accessible name ends with. */
  personName: string;
  personHeader?: ReactNode;
  /** The record a per-record grant is on. */
  target?: ReactNode;
  /**
   * The record's name, or what stands for it, as plain words. Every control's accessible name ends
   * with it, so the switches of a person's twelve proposal rows are told apart.
   */
  targetLabel?: string;
  /** Drawn in place of the level control, as the per-field kinds draw their field chip. */
  levelCell?: ReactNode;
  /**
   * Drawn as the row header in place of the kind. A per-record list on the person page holds one
   * kind, named once above it, so each of its rows leads with the record instead.
   */
  header?: ReactNode;
  /** A line under the kind's "may …" sentence on a person page: "Also granted" on a kind's second grant. */
  kindNote?: string;
  /** What the kind covers for this person, under its "may …" sentence on the person page (#6831). */
  reach?: readonly string[];
  /** True while a change to this row is being saved; its controls wait. */
  pending?: boolean;
  /** The switch: false removes the grant, true grants it again. */
  onSwitch?: (enabled: boolean) => void;
  onChangeLevel?: (level: GrantLevel) => void;
  /** The not-granted row's one-click Grant, which sends the kind's default. */
  onGrant?: () => void;
  /** The page's own actions for the row, after the row's own "Grant again". */
  actions?: readonly RowAction[];
  /**
   * Said under the row, as an alert, when its last change was refused — "Changed by someone else,
   * reloaded". The row has already been redrawn from the server by then.
   */
  alert?: string;
  /** Marks the row with the app's change tint for a moment, after a change to it succeeded. */
  highlight?: boolean;
  /** Asks the directory again about a grantor whose lookup failed. */
  onRetryGrantor?: () => void;
}>;

export default function GrantRow(props: GrantRowProps) {
  const { row, subject, personHeader, target, targetLabel, levelCell, pending, onSwitch, onChangeLevel, onGrant, alert, highlight } =
    props;
  const lockId = useId();
  const kind = kindLabel(row.kind);
  // On a per-record row the person and the record together name what a control changes.
  const personName = targetLabel ? `${props.personName}, ${targetLabel}` : props.personName;
  const removed = row.state === "removed";
  const derived = row.lock?.reason === "derived";

  const rowActions: RowAction[] = [];
  if (removed && onSwitch && row.hasSwitch) {
    rowActions.push({
      key: "grant-again",
      label: GRANT_AGAIN,
      ariaLabel: accessibleNames.grantAgain(kind, personName),
      icon: <RestoreIcon />,
      // A second re-grant while the first is in flight would mint a second live grant.
      disabled: pending,
      onClick: () => onSwitch(true),
    });
  }
  rowActions.push(...(props.actions ?? []));

  const header =
    props.header ??
    (subject === "record" ? (
      personHeader
    ) : (
      <>
        <Typography className="grant-row-kind-name" variant="body2" sx={{ fontWeight: 500 }}>
          {kind}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p">
          {kindAllows(row.kind)}
        </Typography>
        {props.reach?.map((line) => (
          <Typography key={line} variant="caption" color="text.secondary" component="p" data-part="reach">
            {line}
          </Typography>
        ))}
        {props.kindNote && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ fontStyle: "italic" }}>
            {props.kindNote}
          </Typography>
        )}
      </>
    ));

  const levelAriaLabel = accessibleNames.level(row.levelControl ?? "accessLevel", kind, props.personName, targetLabel);

  return (
    <Row role="row" id={row.rowId} data-state={row.state} aria-describedby={row.lock ? lockId : undefined}>
      {highlight && <ChangeHighlightTint animate={!prefersReducedMotion()} aria-hidden="true" data-testid="grant-row-highlight" />}
      <KindPart role="rowheader" data-part="kind">
        {header}
      </KindPart>
      <TargetPart role="cell" data-part="target">
        {target}
      </TargetPart>
      <LevelPart role="cell" data-part="level">
        {levelCell ?? (row.state === "not-granted" && !row.lock ? (
          <Typography variant="body2" color="text.secondary">
            {row.levelText}
          </Typography>
        ) : (
          <LevelControl
            text={row.levelText}
            current={row.level}
            options={row.levelOptions}
            ariaLabel={levelAriaLabel}
            kindName={kind}
            removed={removed}
            derived={derived}
            disabled={pending}
            onChange={onChangeLevel}
            onRemove={row.hasSwitch && onSwitch ? () => onSwitch(false) : undefined}
          />
        ))}
      </LevelPart>
      <SourcePart role="cell" data-part="source">
        {row.lock && (
          <Tooltip title={row.lock.explanation} enterTouchDelay={0} describeChild>
            <Chip
              icon={<LockOutlinedIcon />}
              label={row.lock.word}
              size="small"
              variant="outlined"
              tabIndex={0}
              aria-describedby={lockId}
              sx={derived ? { borderStyle: "dashed" } : undefined}
            />
          </Tooltip>
        )}
        {row.lock && (
          <span id={lockId} style={visuallyHidden}>
            {row.lock.explanation}
          </span>
        )}
        {removed && row.removedText ? (
          <Typography variant="body2" color="text.secondary">
            {row.removedText}
          </Typography>
        ) : (
          row.source && <SourceText source={row.source} kind={kind} personName={personName} onRetryGrantor={props.onRetryGrantor} />
        )}
      </SourcePart>
      <StatePart role="cell" data-part="state">
        {row.hasSwitch && (
          <TouchSwitch
            checked={!removed}
            disabled={pending || !onSwitch}
            onChange={(_, checked) => onSwitch?.(checked)}
            slotProps={{ input: { "aria-label": accessibleNames.switch(kind, personName) } }}
          />
        )}
        {row.grantVerb && onGrant && (
          <QuietButton
            size="small"
            startIcon={<AddIcon />}
            disabled={pending}
            onClick={onGrant}
            aria-label={row.grantVerb === "widen" ? accessibleNames.widen(kind, personName) : accessibleNames.grant(kind, personName)}
          >
            {row.grantVerb === "widen" ? WIDEN_TO_ORGANIZATION : GRANT}
          </QuietButton>
        )}
      </StatePart>
      <ActionsPart role="cell" data-part="actions">
        <RowActions actions={rowActions} collapsed ariaLabel={accessibleNames.actions(kind, personName)} />
      </ActionsPart>
      {alert && (
        <AlertPart role="cell">
          <Alert severity="warning" role="alert" sx={{ py: 0 }}>
            {alert}
          </Alert>
        </AlertPart>
      )}
    </Row>
  );
}
